import * as React from 'react';

import { Colors } from 'theme/colors';
import { FontSize } from 'theme/fonts';
import { NakedIcon } from 'theme/icons';
import { Bold } from 'theme/text';

import { Button, Content, Counter } from './styles';
import { Option, ValuePickerProps } from '../models';

const INPUT_ICON = 15;
const DEFAULT_OPTIONS = [
  {
    key: 'Not Available',
    value: undefined,
  },
];

/**
 * Range picker for selecting an option from a range of values.
 */
export class RangePicker<T> extends React.Component<RangePickerProps<T>, State> {
  public static defaultProps = {
    options: DEFAULT_OPTIONS,
  };

  public constructor(props: RangePickerProps<T>) {
    super(props);

    const selectedIndex = props.options.findIndex(this.getIndexForValue);

    this.state = {
      selectedIndex,
    };
  }

  public shouldComponentUpdate(nextProps: RangePickerProps<T>, nextState: State) {
    return (
      nextProps.selectedValue !== this.props.selectedValue ||
      nextState.selectedIndex !== this.state.selectedIndex
    );
  }

  /**
   * On component update, it updates the `selectedIndex` only if the `selectedValue` changes.
   *
   * @param prevProps - Previous props provided to the component.
   */
  public componentDidUpdate(prevProps: RangePickerProps<T>) {
    if (this.props.selectedValue === prevProps.selectedValue) {
      return;
    }

    this.setState({
      selectedIndex: this.props.options.findIndex(this.getIndexForValue),
    });
  }

  public render() {
    let { disabled, options } = this.props;
    let { selectedIndex } = this.state;
    const maxReached = selectedIndex === options.length - 1;
    const minReached = selectedIndex === 0;
    const previousColor = minReached ? Colors.alto : Colors.white;
    const nextColor = maxReached ? Colors.alto : Colors.white;

    if (selectedIndex < 0) {
      disabled = true;
      options = DEFAULT_OPTIONS;
      selectedIndex = 0;
    }

    return (
      <Content>
        <Button
          backgroundColor={previousColor}
          disabled={disabled || minReached}
          onPress={this.decrement}>
          <NakedIcon name="minus" color={Colors.black} size={INPUT_ICON} />
        </Button>
        <Counter>
          <Bold color={Colors.black} fontSize={FontSize.H4}>
            {options[selectedIndex].key}
          </Bold>
        </Counter>
        <Button
          backgroundColor={nextColor}
          disabled={disabled || maxReached}
          onPress={this.increment}>
          <NakedIcon name="add" color={Colors.black} size={INPUT_ICON} />
        </Button>
      </Content>
    );
  }

  /**
   * Selects the previous option in the range.
   */
  private readonly decrement = () =>
    this.setState({ selectedIndex: this.state.selectedIndex - 1 }, this.handleOnChange);

  /**
   * Finds the index of the first occurrence of a given value in the set of options.
   *
   * TODO: Investigate why this function doesn't match its description.
   *
   * @param option - Option to determine.
   */
  private readonly getIndexForValue = (option: Option<T>) => {
    const { selectedValue } = this.props;

    return option.value === selectedValue;
  };

  /**
   * Emits an `onChange` event with the value of the selected option.
   *
   */
  private readonly handleOnChange = () => {
    const { onChange, options } = this.props;
    const { selectedIndex } = this.state;

    if (onChange) {
      onChange(options[selectedIndex].value);
    }
  };

  /**
   * Selects the next option in the range.
   */
  private readonly increment = () => {
    const { selectedIndex } = this.state;

    this.setState({ selectedIndex: selectedIndex + 1 }, this.handleOnChange);
  };
}

/* Type definitions */
export type RangePickerProps<T> = ValuePickerProps<T>;

interface State {
  /** The index of the currently selected option */
  selectedIndex: number;
}
